/**
 * RegenerateTokenDialog Component (Story 14.2)
 *
 * Confirmation dialog for rotating a portal link's token. The new URL is
 * displayed exactly once via the parent's TokenRevealDialog after success.
 */

import { useMutation } from "@tanstack/react-query";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { motion, AnimatePresence } from "motion/react";
import { Button } from "@provenleap/ui";
import { ArrowsClockwise } from "@phosphor-icons/react";
import {
  regeneratePortalLinkToken,
  type PortalLinkProjection,
  type PortalLinkWithToken,
} from "../../lib/portalLinksApi";
import type { ToastType } from "../../hooks/useToast";

interface RegenerateTokenDialogProps {
  link: PortalLinkProjection;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRegenerated: (link: PortalLinkWithToken) => void;
  showToast: (type: ToastType, message: string) => void;
}

export function RegenerateTokenDialog({
  link,
  open,
  onOpenChange,
  onRegenerated,
  showToast,
}: RegenerateTokenDialogProps) {
  const regen = useMutation({
    mutationFn: () => regeneratePortalLinkToken(link.id),
    onSuccess: (newLink) => onRegenerated(newLink),
    onError: (err: Error) => {
      showToast("error", err.message ?? "Failed to regenerate token");
    },
  });

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild>
              <motion.div
                className="fixed inset-0 bg-black/40 z-50"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              />
            </DialogPrimitive.Overlay>
            <DialogPrimitive.Content asChild>
              <motion.div
                className="fixed left-1/2 top-1/2 z-50 w-full max-w-[440px] -translate-x-1/2 -translate-y-1/2 rounded-3xl bg-background p-8 shadow-lg"
                style={{ border: "0.5px solid var(--hairline)" }}
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              >
                <DialogPrimitive.Title className="text-lg font-semibold">
                  Regenerate share link URL?
                </DialogPrimitive.Title>
                <DialogPrimitive.Description className="text-[13px] text-muted-foreground mt-2">
                  A new URL will be generated for “{link.label}”. The old URL
                  will stop working immediately. Anyone using it will need the
                  new link.
                </DialogPrimitive.Description>
                <div className="flex justify-end gap-3 mt-6">
                  <DialogPrimitive.Close asChild>
                    <Button
                      variant="outline"
                      className="rounded-2xl bg-background cursor-pointer shadow-xs hover:-translate-y-0.5 hover:shadow-sm hover:bg-background transition-all"
                      style={{ border: "0.5px solid var(--hairline)" }}
                      disabled={regen.isPending}
                    >
                      Cancel
                    </Button>
                  </DialogPrimitive.Close>
                  <Button
                    variant="brand"
                    className="rounded-2xl text-[13px] cursor-pointer"
                    onClick={() => regen.mutate()}
                    disabled={regen.isPending}
                    data-testid="confirm-regenerate"
                  >
                    <ArrowsClockwise size={14} className="mr-1" />
                    {regen.isPending ? "Generating…" : "Generate new URL"}
                  </Button>
                </div>
              </motion.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}
